import { Component } from '@theme/component';

/**
 * A click-through stack of full-bleed collage images.
 *
 * Every image fills the collage frame. The front of the stack is scaled down
 * (via transform) to the inset area, floating over the next image in the
 * stack, which shows full-bleed behind it. Clicking the front image sends it
 * to the back of the stack and animates the newly revealed full-bleed image
 * down to the inset size, looping forever. On touch, horizontal swipes
 * anywhere over the collage page it too — left for the next image, right for
 * the previous (the frame's pan-y touch-action leaves vertical scrolling to
 * the browser). With a single image the stack stays inert — no button
 * semantics, no pointer affordance.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stack - The container holding the stacked images.
 * @property {HTMLElement} [dots] - Mobile pagination dot container, populated here.
 *
 * @extends {Component<Refs>}
 */
class HeroCollageComponent extends Component {
  requiredRefs = ['stack'];

  /** Minimum horizontal travel for a pointer gesture to count as a swipe. */
  static #SWIPE_DISTANCE = 30;

  #front = 0;

  /** @type {AbortController | undefined} */
  #controller;

  /** The touch gesture in progress, if any. */
  /** @type {{ id: number, x: number, y: number } | undefined} */
  #swipeStart;

  /** When the last swipe was handled, for the synthesized-click guard. */
  #lastSwipeTime = -Infinity;

  connectedCallback() {
    super.connectedCallback();

    this.#sync();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Swipes are tracked on the component so they work anywhere over the
    // collage (the stack itself only takes pointer events on the front
    // image), and released on window so a flick that ends outside the
    // frame still lands.
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    window.addEventListener('pointerup', this.#handlePointerUp, { signal });
    window.addEventListener('pointercancel', this.#handlePointerCancel, { signal });

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', (event) => this.#handleBlockSelect(event), { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  /**
   * Sends the front image to the back of the stack and shrinks the next one down.
   *
   * @param {Event} [event]
   */
  next(event) {
    // A handled swipe that ends over the front image can still synthesize a
    // click in some browsers — swallow it so the swipe doesn't act twice.
    if (event?.type === 'click' && event.timeStamp - this.#lastSwipeTime < 500) return;

    const images = this.#images;
    if (images.length < 2) return;

    this.#activate((this.#front + 1) % images.length);
  }

  /** Brings the previous image in the loop back to the front. */
  previous() {
    const images = this.#images;
    if (images.length < 2) return;

    this.#activate((this.#front - 1 + images.length) % images.length);
  }

  /**
   * Starts tracking a touch/pen gesture (mouse users click instead).
   *
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || this.#images.length < 2) return;

    this.#swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  /**
   * Pages the stack when the gesture travelled far enough and was
   * predominantly horizontal: left for next, right for previous.
   *
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    const start = this.#swipeStart;
    if (!start || event.pointerId !== start.id) return;

    this.#swipeStart = undefined;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < HeroCollageComponent.#SWIPE_DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;

    this.#lastSwipeTime = event.timeStamp;

    if (dx < 0) {
      this.next();
    } else {
      this.previous();
    }
  };

  /** Drops the gesture when the browser claims it (e.g. vertical scroll). */
  #handlePointerCancel = () => {
    this.#swipeStart = undefined;
  };

  /**
   * Activates the stack from the keyboard when it has button semantics.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.next();
  }

  /**
   * Brings the image of the clicked pagination dot to the front.
   *
   * @param {MouseEvent} event
   */
  select(event) {
    const dot = event.target instanceof Element && event.target.closest('[data-index]');
    if (!(dot instanceof HTMLElement)) return;

    const index = Number(dot.dataset.index);
    if (Number.isNaN(index)) return;

    this.#activate(index);
  }

  /** Applies the stack order and interactive affordances to the current children. */
  #sync() {
    const images = this.#images;
    if (!images.length) return;

    this.#syncDots();
    this.#activate(Math.min(this.#front, images.length - 1));

    const stack = this.refs.stack;
    if (images.length > 1) {
      stack.setAttribute('role', 'button');
      stack.setAttribute('tabindex', '0');
    } else {
      stack.removeAttribute('role');
      stack.removeAttribute('tabindex');
    }
  }

  /**
   * Rebuilds the pagination dots to match the image count. Liquid can't count
   * a block's nested blocks, so the container ships empty and is filled here.
   * A lone image gets no dots (literal labels — BE locales are not synced).
   */
  #syncDots() {
    const { dots } = this.refs;
    if (!dots) return;

    const count = this.#images.length > 1 ? this.#images.length : 0;
    if (dots.childElementCount === count) return;

    dots.replaceChildren(
      ...Array.from({ length: count }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'be-hero-collage__dot';
        dot.setAttribute('aria-label', `Show slide ${index + 1}`);
        dot.setAttribute('data-index', String(index));
        dot.setAttribute('on:click', '/select');
        return dot;
      })
    );
  }

  /**
   * Makes the image at `index` the front of the stack.
   *
   * The outgoing front is sent to the back, where the z-order swap hides it
   * behind the full-bleed images above while its transform resets without
   * transitioning — so only the incoming image is seen animating down from
   * full-bleed to the inset size.
   *
   * @param {number} index
   */
  #activate(index) {
    const images = this.#images;
    const front = images[index];
    if (!front) return;

    this.#front = index;

    for (const image of images) {
      if (image === front || !image.classList.contains('is-front')) continue;
      image.classList.add('is-resetting');
      image.classList.remove('is-front');
    }

    front.classList.remove('is-resetting');
    front.classList.add('is-front');

    // Stack order, front to back: the front image, then the ones after it in
    // DOM order, wrapping around — so each outgoing front loops to the back.
    images.forEach((image, i) => {
      const depth = (i - index + images.length) % images.length;
      image.style.zIndex = String(images.length - depth);
    });

    // Re-enable transitions once the instant transform reset has painted.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        for (const image of this.#images) {
          if (!image.classList.contains('is-front')) image.classList.remove('is-resetting');
        }
      })
    );

    const { dots } = this.refs;
    if (dots) {
      for (const [dotIndex, dot] of Array.from(dots.children).entries()) {
        if (dotIndex === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }
    }
  }

  /**
   * Brings the inset the merchant selected in the theme editor to the front.
   *
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const index = this.#images.findIndex((image) => image.contains(target) || image === target);
    if (index >= 0) this.#activate(index);
  }

  /** @returns {HTMLElement[]} */
  get #images() {
    return Array.from(this.refs.stack.children).filter((child) => child instanceof HTMLElement);
  }
}

if (!customElements.get('hero-collage-component')) {
  customElements.define('hero-collage-component', HeroCollageComponent);
}
